import type { MedplumClient } from '@medplum/core';
import type {
  Consent,
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';
import {
  COORDINATION_OF_CARE_ROI_LINK_IDS,
  COORDINATION_OF_CARE_ROI_QUESTIONNAIRE_URL,
  ROI_CONSENT_REF_EXT,
} from '@unify/fhir-model/constants';
import { buildRoiConsent, type RoiPurposeCode } from '@unify/fhir-model/resources/consent';
import { getPatientOfficialName } from '../../pages/clients/utils/clientProfileFhir';
import { shapeResponseItemsForQuestionnaire, indexResponseItemsByLinkId } from '../../pages/appointments/utils/questionnaireResponseShape';
import { applyChartDocumentSignSettings, setChartDocumentStatus } from './chartDocumentWorkflow';
import { toFhirDateOnlyLocal } from '../dateHelpers';

/** Item control code for the chart contact picker on To/From fields. */
export const CHART_CONTACT_PICKER_ITEM_CONTROL = 'chart-contact-picker';

/** Prefix for RelatedPerson options in the contact picker (`rp:{id}`). */
export const CHART_CONTACT_PICKER_RP_PREFIX = 'rp:';

/** Prefix for Patient.contact options in the contact picker (`pc:{index}`). */
export const CHART_CONTACT_PICKER_PC_PREFIX = 'pc:';

export const CHART_CONTACT_PICKER_OTHER_CODE = 'other';

const QUESTIONNAIRE_PURPOSE_TO_ROI: Record<string, RoiPurposeCode> = {
  'medical-history-and-evaluation-s': 'medicalHistoryAndEvaluations',
  'mental-health-evaluations': 'mentalHealthEvaluations',
  'developmental-and-or-social-history': 'developmentalAndSocialHistory',
  'educational-records': 'educationalRecords',
  'progress-notes-and-treatment-or-closing': 'progressNotesAndTreatmentSummary',
  other: 'other',
};

export function isCoordinationOfCareRoiTemplate(questionnaire: Questionnaire | undefined): boolean {
  if (!questionnaire) return false;
  if (questionnaire.url === COORDINATION_OF_CARE_ROI_QUESTIONNAIRE_URL) return true;
  const templateId = questionnaire.identifier?.find(
    (id) => id.system === 'https://unify.health/fhir/questionnaire-template',
  )?.value;
  return templateId === 'meridian-counseling-release-of-information-coordination-of-care';
}

export function todayIsoDate(): string {
  return toFhirDateOnlyLocal(new Date());
}

export function mapQuestionnairePurposeCodesToRoi(codes: string[]): RoiPurposeCode[] {
  const mapped = codes
    .map((code) => QUESTIONNAIRE_PURPOSE_TO_ROI[code])
    .filter((code): code is RoiPurposeCode => Boolean(code));
  return [...new Set(mapped)];
}

function stringItem(linkId: string, value: string): QuestionnaireResponseItem {
  return { linkId, answer: [{ valueString: value }] };
}

function dateItem(linkId: string, value: string): QuestionnaireResponseItem {
  return { linkId, answer: [{ valueDate: value }] };
}

/** Provider/client prefills for coordination-of-care ROI chart documents. */
export function buildCoordinationRoiPrefillItems(patientDisplayName: string): QuestionnaireResponseItem[] {
  const today = todayIsoDate();
  return [
    stringItem(COORDINATION_OF_CARE_ROI_LINK_IDS.clientName, patientDisplayName),
    dateItem(COORDINATION_OF_CARE_ROI_LINK_IDS.signatureDate, today),
    dateItem(COORDINATION_OF_CARE_ROI_LINK_IDS.witnessDate, today),
  ];
}

export function mergeCoordinationRoiPrefillIntoResponse(
  questionnaire: Questionnaire,
  qr: QuestionnaireResponse,
  patientDisplayName: string,
): QuestionnaireResponse {
  const prefills = buildCoordinationRoiPrefillItems(patientDisplayName);
  const byLinkId = indexResponseItemsByLinkId(qr.item ?? []);
  for (const item of prefills) {
    if (!byLinkId.has(item.linkId!)) {
      byLinkId.set(item.linkId!, item);
    }
  }
  const flatItems = [...byLinkId.values()];
  const item = shapeResponseItemsForQuestionnaire(flatItems, questionnaire) ?? flatItems;
  return { ...qr, item };
}

/** Prefilled ROI fields that stay editable (never provider-locked on send). */
export function coordinationRoiPrefillLinkIds(): readonly string[] {
  return [
    COORDINATION_OF_CARE_ROI_LINK_IDS.clientName,
    COORDINATION_OF_CARE_ROI_LINK_IDS.signatureDate,
    COORDINATION_OF_CARE_ROI_LINK_IDS.witnessDate,
  ];
}

/** Client portal read-only set — prefilled name/date fields remain editable inputs. */
export function resolveCoordinationRoiClientReadOnlyLinkIds(
  questionnaire: Questionnaire,
  baseLocked: ReadonlySet<string>,
): Set<string> {
  if (!isCoordinationOfCareRoiTemplate(questionnaire)) {
    return new Set(baseLocked);
  }
  const editable = new Set(baseLocked);
  for (const linkId of coordinationRoiPrefillLinkIds()) {
    editable.delete(linkId);
  }
  return editable;
}

export async function attachDraftRoiConsentToChartDocument(
  medplum: MedplumClient,
  params: {
    patientId: string;
    questionnaireResponse: QuestionnaireResponse;
  },
): Promise<{ consent: Consent; questionnaireResponse: QuestionnaireResponse }> {
  const { patientId, questionnaireResponse } = params;
  const existingRef = questionnaireResponse.extension?.find((e) => e.url === ROI_CONSENT_REF_EXT)
    ?.valueReference?.reference;
  if (existingRef?.startsWith('Consent/')) {
    const consentId = existingRef.replace('Consent/', '');
    const consent = await medplum.readResource('Consent', consentId);
    return { consent, questionnaireResponse };
  }

  const consentResource = buildRoiConsent({
    patientId,
    purposeCodes: [],
    status: 'draft',
  });
  const consent = await medplum.createResource(consentResource);
  if (!consent.id) {
    throw new Error('Failed to create ROI consent record.');
  }

  const updatedQr = await medplum.updateResource<QuestionnaireResponse>({
    ...questionnaireResponse,
    extension: [
      ...(questionnaireResponse.extension ?? []).filter((e) => e.url !== ROI_CONSENT_REF_EXT),
      { url: ROI_CONSENT_REF_EXT, valueReference: { reference: `Consent/${consent.id}` } },
    ],
  });

  return { consent, questionnaireResponse: updatedQr };
}

export async function loadPatientDisplayName(
  medplum: MedplumClient,
  patientId: string,
): Promise<string> {
  const patient = await medplum.readResource('Patient', patientId);
  return getPatientOfficialName(patient);
}

/** Ensures ROI prefilled client name and dates exist on a chart document response. */
export async function ensureCoordinationRoiPrefillOnResponse(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  qr: QuestionnaireResponse,
): Promise<QuestionnaireResponse> {
  if (!isCoordinationOfCareRoiTemplate(questionnaire)) return qr;
  const patientRef = qr.subject?.reference;
  if (!patientRef?.startsWith('Patient/')) return qr;
  const patientId = patientRef.replace('Patient/', '');
  const displayName = await loadPatientDisplayName(medplum, patientId);
  return mergeCoordinationRoiPrefillIntoResponse(questionnaire, qr, displayName);
}

export function extractChoiceCodes(item: QuestionnaireResponseItem | undefined): string[] {
  return (item?.answer ?? [])
    .map((a) => a.valueCoding?.code ?? a.valueString ?? '')
    .filter(Boolean);
}

export function extractStringAnswer(item: QuestionnaireResponseItem | undefined): string | undefined {
  const raw = item?.answer?.[0]?.valueString ?? item?.answer?.[0]?.valueCoding?.display;
  return raw?.trim() || undefined;
}

export function parseCoordinationRoiContactSelection(code: string | undefined): {
  kind: 'related-person' | 'patient-contact' | 'other';
  relatedPersonId?: string;
  patientContactIndex?: number;
} {
  if (!code || code === CHART_CONTACT_PICKER_OTHER_CODE) {
    return { kind: 'other' };
  }
  if (code.startsWith(CHART_CONTACT_PICKER_RP_PREFIX)) {
    return {
      kind: 'related-person',
      relatedPersonId: code.slice(CHART_CONTACT_PICKER_RP_PREFIX.length),
    };
  }
  if (code.startsWith(CHART_CONTACT_PICKER_PC_PREFIX)) {
    const index = Number.parseInt(code.slice(CHART_CONTACT_PICKER_PC_PREFIX.length), 10);
    return {
      kind: 'patient-contact',
      patientContactIndex: Number.isNaN(index) ? undefined : index,
    };
  }
  return { kind: 'other' };
}

export function buildChartContactPickerOptionValue(contact: {
  source: 'patient-contact' | 'related-person';
  id: string;
  relatedPersonId?: string;
}): string {
  if (contact.source === 'related-person' && contact.relatedPersonId) {
    return `${CHART_CONTACT_PICKER_RP_PREFIX}${contact.relatedPersonId}`;
  }
  if (contact.id.startsWith('pc-')) {
    return `${CHART_CONTACT_PICKER_PC_PREFIX}${contact.id.replace('pc-', '')}`;
  }
  return CHART_CONTACT_PICKER_OTHER_CODE;
}

export function applyCoordinationRoiClientSendSettings(qr: QuestionnaireResponse): QuestionnaireResponse {
  let next = applyChartDocumentSignSettings(qr, {
    providerSignRequired: false,
    clientSignRequired: true,
  });
  next = setChartDocumentStatus(next, 'pending-client');
  return next;
}
